import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Stack,
    LinearProgress,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
} from '@mui/material';

import { useTranslation } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
interface ImportOSMLocationsModalProps {
    open: boolean;
    onClose: () => void;
    onImport: (selected: string[], polygon: any) => void;
    loading: boolean;
    polygon: any;
}

const ImportOSMLocationsModal: React.FC<ImportOSMLocationsModalProps> = ({ open, onClose, onImport, loading, polygon }) => {
    const { t } = useTranslation(['map', 'common']);
    const OTHER_POIS = [
        { label: t('modals.importOsm.restaurants'), value: 'amenity=restaurant' },
        { label: t('modals.importOsm.cafes'), value: 'amenity=cafe' },
        { label: t('modals.importOsm.parks'), value: 'leisure=park' },
        { label: t('modals.importOsm.banks'), value: 'amenity=bank' },
        { label: t('modals.importOsm.postOffices'), value: 'amenity=post_office' },
        { label: t('modals.importOsm.atms'), value: 'amenity=atm' },
        { label: t('modals.importOsm.parking'), value: 'amenity=parking' },
        { label: t('modals.importOsm.policeStations'), value: 'amenity=police' },
        { label: t('modals.importOsm.fireStations'), value: 'amenity=fire_station' },
        { label: t('modals.importOsm.hotels'), value: 'tourism=hotel' },
        { label: t('modals.importOsm.museums'), value: 'tourism=museum' },
        { label: t('modals.importOsm.pubs'), value: 'amenity=pub' },
        { label: t('modals.importOsm.bars'), value: 'amenity=bar' },
        { label: t('modals.importOsm.pharmacies'), value: 'amenity=pharmacy' },
        { label: t('modals.importOsm.placesOfWorship'), value: 'amenity=place_of_worship' },
    ];
    const MAIN_POIS = [
        { label: t('modals.importOsm.schools'), value: 'amenity=school', icon: <Icon size={20} d={ICON_FALLBACKS.School} /> },
        { label: t('modals.importOsm.hospitals'), value: 'amenity=hospital', icon: <Icon size={20} d={ICON_FALLBACKS.LocalHospital} /> },
        { label: t('modals.importOsm.supermarkets'), value: 'shop=supermarket', icon: <Icon size={20} d={ICON_FALLBACKS.LocalGroceryStore} /> },
        { label: t('modals.importOsm.playgrounds'), value: 'leisure=playground', icon: <Icon size={20} d={ICON_FALLBACKS.SportsSoccer} /> },
        { label: t('modals.importOsm.libraries'), value: 'amenity=library', icon: <Icon size={20} d={ICON_FALLBACKS.LocalLibrary} /> },
    ];
    const [selectedMain, setSelectedMain] = useState<string[]>([]);
    const [selectedOther, setSelectedOther] = useState<string[]>([]);

    const handleMainClick = (value: string) => {
        setSelectedMain(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    };

    const handleOtherChange = (event: any) => {
        setSelectedOther(event.target.value);
    };

    const handleImport = () => {
        onImport([...selectedMain, ...selectedOther], polygon);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{t('modals.importOsm.title')}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {t('modals.importOsm.description')}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    {MAIN_POIS.map(poi => (
                        <Box key={poi.value} textAlign="center">
                            <IconButton
                                color={selectedMain.includes(poi.value) ? 'primary' : 'default'}
                                onClick={() => handleMainClick(poi.value)}
                            >
                                {poi.icon}
                            </IconButton>
                            <Typography variant="caption">{poi.label}</Typography>
                        </Box>
                    ))}
                </Stack>
                <FormControl fullWidth>
                    <InputLabel>{t('modals.importOsm.otherLocations')}</InputLabel>
                    <Select
                        multiple
                        value={selectedOther}
                        onChange={handleOtherChange}
                        renderValue={(selected) =>
                            (selected as string[]).map(val => {
                                const found = OTHER_POIS.find(p => p.value === val);
                                return found ? found.label : val;
                            }).join(', ')
                        }
                    >
                        {OTHER_POIS.map(poi => (
                            <MenuItem key={poi.value} value={poi.value}>
                                <Checkbox checked={selectedOther.includes(poi.value)} />
                                <ListItemText primary={poi.label} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {loading && <Box sx={{ mt: 2 }}><LinearProgress /></Box>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>{t('common:cancel')}</Button>
                <Button onClick={handleImport} variant="contained" disabled={loading || (!selectedMain.length && !selectedOther.length)}>
                    {t('modals.importOsm.import')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImportOSMLocationsModal; 